import {
  suppliesInventoryConfigIdFormat,
  suppliesInventoryReorderIdFormat,
} from "@redotech/redo-marketplace-id";
import { OpensearchPaginationCursorSchema } from "@redotech/redo-views-common/table";
import * as z from "zod";
import { SuppliesInventoryConfigRowSchema } from "./get-supplies-inventory-configs";

const SuppliesInventoryReorderRowSchema = z.object({
  reorderId: suppliesInventoryReorderIdFormat.schema,
  // Mongo Order ObjectId (hex) of the order placed for this reorder.
  orderId: z.string(),
  orderName: z.string().nullable(),
  date: z.string(),
  quantityOrderedCases: z.number(),
  fulfillmentStatus: z.string().nullable(),
  // Null until the reorder has been received/restocked.
  receivedDate: z.string().nullable(),
});

export type SuppliesInventoryReorderRow = z.infer<
  typeof SuppliesInventoryReorderRowSchema
>;

const SuppliesLabelInventoryChangeRowSchema = z.object({
  id: z.string(),
  date: z.string(),
  // Pretty batch id from the print job's fulfillment batch. Null when the row
  // has no print job (e.g. restocks) or the print job has only group ids.
  prettyBatchId: z.string().nullable(),
  // Signed inventory delta (negative = consumed, positive = restocked).
  delta: z.number(),
  notes: z.string().nullable(),
  referenceId: z.string().nullable(),
  userName: z.string().nullable(),
  // Deduped order names from the print job's batch / fulfillment groups.
  orderNames: z.array(z.string()),
  // Label PDF url from the print job; opens in a new tab.
  pdfUrl: z.string().nullable(),
});

export type SuppliesLabelInventoryChangeRow = z.infer<
  typeof SuppliesLabelInventoryChangeRowSchema
>;

const SuppliesParcelInventoryChangeRowSchema = z.object({
  id: z.string(),
  date: z.string(),
  // Fulfillment group id from the parcel-consume reference; drives the
  // "Go to shipment" link. Null for rows without a shipment (e.g. restocks).
  fulfillmentGroupId: z.string().nullable(),
  delta: z.number(),
  referenceId: z.string().nullable(),
  userName: z.string().nullable(),
  orderNames: z.array(z.string()),
  // OMS parcel name(s) tied to this config used on the shipment.
  parcelNames: z.array(z.string()),
});

export type SuppliesParcelInventoryChangeRow = z.infer<
  typeof SuppliesParcelInventoryChangeRowSchema
>;

export const getSuppliesInventoryConfigDetail = {
  name: "getSuppliesInventoryConfigDetail" as const,
  input: z.object({ configId: suppliesInventoryConfigIdFormat.schema }),
  output: z.object({ config: SuppliesInventoryConfigRowSchema }),
};

export const getSuppliesInventoryConfigReorders = {
  name: "getSuppliesInventoryConfigReorders" as const,
  input: z.object({
    configId: suppliesInventoryConfigIdFormat.schema,
    pageNumber: z.number(),
    pageSize: z.number(),
  }),
  output: z.object({ reorders: z.array(SuppliesInventoryReorderRowSchema) }),
};

export const getSuppliesInventoryConfigReordersCount = {
  name: "getSuppliesInventoryConfigReordersCount" as const,
  input: z.object({ configId: suppliesInventoryConfigIdFormat.schema }),
  output: z.object({ total: z.number() }),
};

export const getSuppliesInventoryConfigLabelHistory = {
  name: "getSuppliesInventoryConfigLabelHistory" as const,
  input: z.object({
    configId: suppliesInventoryConfigIdFormat.schema,
    pageNumber: z.number(),
    pageSize: z.number(),
    opensearchPaginationCursor: OpensearchPaginationCursorSchema.nullish(),
  }),
  output: z.object({
    changes: z.array(SuppliesLabelInventoryChangeRowSchema),
    opensearchPaginationCursor: OpensearchPaginationCursorSchema.nullish(),
  }),
};

export const getSuppliesInventoryConfigInventoryHistoryCount = {
  name: "getSuppliesInventoryConfigInventoryHistoryCount" as const,
  input: z.object({ configId: suppliesInventoryConfigIdFormat.schema }),
  output: z.object({ total: z.number() }),
};

// Parcel-packaging variant of the change history. Reuses the type-agnostic
// count RPC above (scoped by product + location).
export const getSuppliesInventoryConfigParcelHistory = {
  name: "getSuppliesInventoryConfigParcelHistory" as const,
  input: z.object({
    configId: suppliesInventoryConfigIdFormat.schema,
    pageNumber: z.number(),
    pageSize: z.number(),
    opensearchPaginationCursor: OpensearchPaginationCursorSchema.nullish(),
  }),
  output: z.object({
    changes: z.array(SuppliesParcelInventoryChangeRowSchema),
    opensearchPaginationCursor: OpensearchPaginationCursorSchema.nullish(),
  }),
};

export const markSuppliesReorderReceived = {
  name: "markSuppliesReorderReceived" as const,
  input: z.object({ reorderId: suppliesInventoryReorderIdFormat.schema }),
  output: z.object({}),
};
